import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import RoleBasedNavigation from "../../components/ui/RoleBasedNavigation";
import { generateSurveyReport } from "../customer-database/utils/generateSurveyReport";
import Button from "../../components/ui/Button";
import Icon from "../../components/AppIcon";
import WorkflowProgressIndicator from "../../components/ui/WorkflowProgressIndicator";
import Input from "../../components/ui/Input";
import Select from "../../components/ui/Select";
import {
  customers,
  surveys,
  users,
  transportWork,
  civilWork,
  technicalWork,
  commissioningWork,
} from "../../lib/database";
import {
  getWorkSubmittedAt,
  getWorkSubmitterId,
} from "../../lib/workSubmissionMeta";
import InformationView from "./components/InformationView";
import SurveyView from "./components/SurveyView";
import TransportView from "./components/TransportView";
import CivilView from "./components/CivilView";
import TechnicalView from "./components/TechnicalView";
import CommissioningView from "./components/CommissioningView";

const CustomerDetail = () => {
  const navigate = useNavigate();
  const { customerId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const downloadTriggeredRef = useRef(false);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("information");
  const [customer, setCustomer] = useState(null);
  const [survey, setSurvey] = useState(null);
  const [transportWorkData, setTransportWorkData] = useState(null);
  const [civilWorkData, setCivilWorkData] = useState(null);
  const [technicalWorkData, setTechnicalWorkData] = useState(null);
  const [commissioningWorkData, setCommissioningWorkData] = useState(null);
  const [workersById, setWorkersById] = useState(new Map());
  const [isEditingInfo, setIsEditingInfo] = useState(false);
  const [editForm, setEditForm] = useState({
    name: "",
    dob: "",
    aadhaar_number: "",
    phone_number: "",
    address: "",
    village: "",
    district: "",
    state: "",
    pincode: "",
  });
  const [editErrors, setEditErrors] = useState({});
  const [isSavingCustomer, setIsSavingCustomer] = useState(false);
  const [editSubmitError, setEditSubmitError] = useState(null);

  useEffect(() => {
    downloadTriggeredRef.current = false;
    fetchData();
  }, [customerId]);

  useEffect(() => {
    if (customer) {
      setEditForm({
        name: customer.name || "",
        dob: customer.dob || "",
        aadhaar_number: customer.aadhaar_number || "",
        phone_number: customer.phone_number || "",
        address: customer.address || "",
        village: customer.village || "",
        district: customer.district || "",
        state: customer.state || "",
        pincode: customer.pincode || "",
      });
      setEditErrors({});
      setEditSubmitError(null);
    }
  }, [customer]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [
        customerData,
        surveyData,
        transportData,
        civilData,
        technicalData,
        commissioningData,
        workersData,
      ] = await Promise.all([
          customers.getById(customerId),
          surveys.getByCustomerId(customerId).catch(() => null),
          transportWork.getByCustomerId(customerId).catch(() => null),
          civilWork.getByCustomerId(customerId).catch(() => null),
          technicalWork.getByCustomerId(customerId).catch(() => null),
          commissioningWork.getByCustomerId(customerId).catch(() => null),
          users.getAll().catch(() => []),
        ]);

      setCustomer(customerData);
      setSurvey(surveyData);
      setTransportWorkData(transportData);
      setCivilWorkData(civilData);
      setTechnicalWorkData(technicalData);
      setCommissioningWorkData(commissioningData);
      setWorkersById(
        new Map((workersData || []).map((worker) => [worker.id, worker])),
      );
    } catch (error) {
      console.error("Error fetching customer detail:", error);
    } finally {
      setLoading(false);
    }
  };

  // When opening with ?download=survey-pdf (e.g. from Excel export link), generate and download the PDF
  useEffect(() => {
    if (loading || !customer || !customerId) return;
    const wantDownload = searchParams.get("download") === "survey-pdf";
    if (!wantDownload) return;
    if (downloadTriggeredRef.current) return;
    if (!survey) {
      setSearchParams({}, { replace: true });
      return;
    }
    downloadTriggeredRef.current = true;
    generateSurveyReport(customer, survey, null, null)
      .then(() => {
        setSearchParams({}, { replace: true });
      })
      .catch((err) => {
        console.error("Survey report download failed:", err);
        setSearchParams({}, { replace: true });
      });
  }, [loading, customer, survey, customerId, searchParams, setSearchParams]);

  const stateOptions = [
    { value: "Andhra Pradesh", label: "Andhra Pradesh" },
    { value: "Telangana", label: "Telangana" },
  ];

  const validateEditForm = () => {
    const errors = {};

    if (!editForm.name?.trim()) {
      errors.name = "Name is required";
    }

    if (
      editForm.aadhaar_number &&
      !/^\d{12}$/.test(editForm.aadhaar_number.replace(/\s/g, ""))
    ) {
      errors.aadhaar_number = "Aadhaar must be 12 digits";
    }

    if (!editForm.phone_number?.trim()) {
      errors.phone_number = "Phone number is required";
    } else if (!/^\d{10}$/.test(editForm.phone_number.replace(/\s/g, ""))) {
      errors.phone_number = "Phone must be 10 digits";
    }

    setEditErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleEditFieldChange = (field, value) => {
    if (field === "phone_number") {
      value = String(value).replace(/\D/g, "").slice(0, 10);
    } else if (field === "aadhaar_number") {
      value = String(value).replace(/\D/g, "").slice(0, 12);
    }
    setEditForm((prev) => ({ ...prev, [field]: value }));
    if (editErrors[field]) {
      setEditErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const handleSaveCustomer = async (e) => {
    e.preventDefault();
    setEditSubmitError(null);

    if (!validateEditForm()) return;

    try {
      setIsSavingCustomer(true);
      const cleanedData = {
        name: editForm.name?.trim() || "",
        aadhaar_number: editForm.aadhaar_number || null,
        phone_number: editForm.phone_number || null,
        dob: editForm.dob || null,
        address: editForm.address || null,
        village: editForm.village || null,
        district: editForm.district || null,
        state: editForm.state || null,
        pincode: editForm.pincode || null,
      };

      await customers.update(customer.id, cleanedData);
      await fetchData();
      setIsEditingInfo(false);
    } catch (err) {
      console.error("Error updating customer:", err);
      setEditSubmitError(
        err?.message || "Failed to update customer. Please try again.",
      );
    } finally {
      setIsSavingCustomer(false);
    }
  };

  const tabs = [
    { id: "information", label: "Information", icon: "User" },
    { id: "survey", label: "Survey", icon: "ClipboardList" },
    { id: "transport", label: "Transport", icon: "Truck" },
    { id: "civil", label: "Civil", icon: "HardHat" },
    { id: "technical", label: "Technical", icon: "Wrench" },
    { id: "commissioning", label: "Commissioning", icon: "FlaskConical" },
  ];

  const getSubmissionMeta = (row, phase) => {
    if (!row) return { submittedAt: null, submitterLabel: "—" };

    const submittedAt = getWorkSubmittedAt(row, phase);
    const submitterId = getWorkSubmitterId(row);
    const submitter = submitterId ? workersById.get(submitterId) : null;
    const submitterLabel = submitter
      ? `${submitter.name || "Unnamed"}${submitter.unique_id ? ` (${submitter.unique_id})` : ""}`
      : submitterId || "—";

    return { submittedAt, submitterLabel };
  };

  const getActivePhaseSubmissionMeta = () => {
    if (activeTab === "survey") return getSubmissionMeta(survey, "survey");
    if (activeTab === "transport")
      return getSubmissionMeta(transportWorkData, "transport");
    if (activeTab === "civil") return getSubmissionMeta(civilWorkData, "civil");
    if (activeTab === "technical")
      return getSubmissionMeta(technicalWorkData, "technical");
    if (activeTab === "commissioning")
      return getSubmissionMeta(commissioningWorkData, "commissioning");
    return null;
  };

  const getCurrentPhaseFromStatus = (status) => {
    if (!status) return "survey";

    if (status === "survey_not_started" || status === "survey_inprogress") {
      return "survey";
    }

    if (status === "survey_complete" || status === "civil_inprogress") {
      return "civil";
    }

    if (
      status === "civil_complete" ||
      status === "technical_inprogress" ||
      status === "technical_complete"
    ) {
      return "technical";
    }

    // Fallback
    return "survey";
  };

  const getCompletionDataFromStatus = (status) => {
    const base = { survey: 0, civil: 0, technical: 0 };

    if (!status) return base;

    if (status === "survey_inprogress") {
      return { ...base, survey: 50 };
    }

    if (status === "survey_complete") {
      return { ...base, survey: 100 };
    }

    if (status === "civil_inprogress") {
      return { ...base, survey: 100, civil: 50 };
    }

    if (status === "civil_complete") {
      return { ...base, survey: 100, civil: 100 };
    }

    if (status === "technical_inprogress") {
      return { ...base, survey: 100, civil: 100, technical: 50 };
    }

    if (status === "technical_complete") {
      return { ...base, survey: 100, civil: 100, technical: 100 };
    }

    return base;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <RoleBasedNavigation />
        <div className="content-area flex items-center justify-center">
          <p className="text-muted-foreground">Loading customer details...</p>
        </div>
      </div>
    );
  }

  if (!customer) {
    return (
      <div className="min-h-screen bg-background">
        <RoleBasedNavigation />
        <div className="content-area flex items-center justify-center">
          <div className="text-center">
            <Icon
              name="AlertCircle"
              size={48}
              className="mx-auto mb-4 text-error"
            />
            <p className="text-error">Customer not found</p>
            <Button
              variant="outline"
              onClick={() => navigate(-1)}
              className="mt-4"
            >
              Go Back
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const renderContent = () => {
    switch (activeTab) {
      case "information":
        if (!isEditingInfo) {
          return (
            <InformationView
              customer={customer}
              onEditClick={() => setIsEditingInfo(true)}
            />
          );
        }
        return (
          <form onSubmit={handleSaveCustomer} className="space-y-6">
            {editSubmitError && (
              <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
                <p className="text-sm text-destructive">{editSubmitError}</p>
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Full Name"
                type="text"
                required
                value={editForm.name}
                onChange={(e) => handleEditFieldChange("name", e.target.value)}
                error={editErrors.name}
                placeholder="Enter customer name"
              />

              <Input
                label="Date of Birth"
                type="date"
                value={editForm.dob}
                onChange={(e) => handleEditFieldChange("dob", e.target.value)}
                error={editErrors.dob}
              />

              <Input
                label="Aadhaar Number"
                type="text"
                inputMode="numeric"
                value={editForm.aadhaar_number}
                onChange={(e) =>
                  handleEditFieldChange("aadhaar_number", e.target.value)
                }
                error={editErrors.aadhaar_number}
                placeholder="12 digit Aadhaar number"
                maxLength={12}
              />

              <Input
                label="Phone Number"
                type="text"
                inputMode="numeric"
                required
                value={editForm.phone_number}
                onChange={(e) =>
                  handleEditFieldChange("phone_number", e.target.value)
                }
                error={editErrors.phone_number}
                placeholder="10 digit phone number"
                maxLength={10}
              />

              <Input
                label="Village"
                type="text"
                value={editForm.village}
                onChange={(e) =>
                  handleEditFieldChange("village", e.target.value)
                }
                error={editErrors.village}
                placeholder="Village"
              />

              <Input
                label="District"
                type="text"
                value={editForm.district}
                onChange={(e) =>
                  handleEditFieldChange("district", e.target.value)
                }
                error={editErrors.district}
                placeholder="District"
              />

              <Select
                label="State"
                options={stateOptions}
                value={editForm.state}
                onChange={(value) => handleEditFieldChange("state", value)}
                error={editErrors.state}
                placeholder="Select state"
              />

              <Input
                label="Pincode"
                type="number"
                value={editForm.pincode}
                onChange={(e) =>
                  handleEditFieldChange("pincode", e.target.value)
                }
                error={editErrors.pincode}
                placeholder="Pincode"
              />
            </div>

            <Input
              label="Address"
              type="text"
              value={editForm.address}
              onChange={(e) => handleEditFieldChange("address", e.target.value)}
              error={editErrors.address}
              placeholder="Full address"
            />

            <div className="flex items-center justify-end gap-3 pt-4 border-t border-border">
              <Button
                variant="outline"
                type="button"
                onClick={() => {
                  setIsEditingInfo(false);
                  if (customer) {
                    setEditForm({
                      name: customer.name || "",
                      dob: customer.dob || "",
                      aadhaar_number: customer.aadhaar_number || "",
                      phone_number: customer.phone_number || "",
                      address: customer.address || "",
                      village: customer.village || "",
                      district: customer.district || "",
                      state: customer.state || "",
                      pincode: customer.pincode || "",
                    });
                    setEditErrors({});
                    setEditSubmitError(null);
                  }
                }}
                disabled={isSavingCustomer}
              >
                Cancel
              </Button>
              <Button
                variant="default"
                type="submit"
                loading={isSavingCustomer}
                disabled={isSavingCustomer}
              >
                Save Changes
              </Button>
            </div>
          </form>
        );
      case "survey":
        return <SurveyView survey={survey} />;
      case "transport":
        return (
          <TransportView customer={customer} transportWork={transportWorkData} />
        );
      case "civil":
        return <CivilView customer={customer} civilWork={civilWorkData} />;
      case "technical":
        return (
          <TechnicalView
            customer={customer}
            technicalWork={technicalWorkData}
          />
        );
      case "commissioning":
        return (
          <CommissioningView
            customer={customer}
            commissioningWork={commissioningWorkData}
          />
        );
      default:
        return <InformationView customer={customer} />;
    }
  };

  const activePhaseSubmissionMeta = getActivePhaseSubmissionMeta();

  return (
    <div className="min-h-screen bg-background">
      <RoleBasedNavigation />
      <WorkflowProgressIndicator
        currentPhase={getCurrentPhaseFromStatus(customer.status)}
        completionData={getCompletionDataFromStatus(customer.status)}
      />
      <div className="content-area-with-progress">
        <div className="max-w-[1600px] mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8">
          {/* Header */}
          <div className="mb-6 md:mb-8">
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Icon name="User" size={24} className="text-primary" />
                </div>
                <div>
                  <h1 className="text-2xl md:text-3xl lg:text-4xl font-semibold text-foreground">
                    {customer.name}
                  </h1>
                  <div className="flex flex-wrap items-center gap-3 md:gap-4 mt-1">
                    <p className="text-sm md:text-base text-muted-foreground">
                      CIN:{" "}
                      <span className="font-mono font-medium text-foreground">
                        {customer.cin}
                      </span>
                    </p>
                    {customer.usn && (
                      <>
                        <span className="text-muted-foreground">•</span>
                        <p className="text-sm md:text-base text-muted-foreground">
                          USN:{" "}
                          <span className="font-mono font-medium text-foreground">
                            {customer.usn}
                          </span>
                        </p>
                      </>
                    )}
                  </div>
                </div>
              </div>

              <Button
                variant="ghost"
                iconName="ArrowLeft"
                iconPosition="left"
                onClick={() => navigate(-1)}
              >
                Go Back
              </Button>
            </div>
          </div>

          {/* Main Content with Sidebar */}
          <div className="flex flex-col lg:flex-row gap-6">
            {/* Left Sidebar Navigation */}
            <div className="lg:w-64 flex-shrink-0">
              <div className="bg-card rounded-lg border border-border p-2 sticky top-4">
                <nav className="space-y-1">
                  {tabs.map((tab) => {
                    const isActive = activeTab === tab.id;
                    return (
                      <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-all ${
                          isActive
                            ? "bg-primary/10 text-primary border border-primary/20"
                            : "text-muted-foreground hover:bg-muted hover:text-foreground"
                        }`}
                      >
                        <Icon name={tab.icon} size={20} />
                        <span className="font-medium">{tab.label}</span>
                      </button>
                    );
                  })}
                </nav>
              </div>
            </div>

            {/* Right Content Area */}
            <div className="flex-1 min-w-0">
              <div className="bg-card rounded-lg border border-border p-4 md:p-6 lg:p-8">
                {renderContent()}
                {activePhaseSubmissionMeta && (
                  <div className="shrink-0 flex flex-wrap items-center justify-between gap-3 mt-6 pt-4 border-t border-border bg-muted/30 rounded-md px-3 py-2">
                    <div className="text-xs text-muted-foreground">
                      <p>
                        Submitted at:{" "}
                        <span className="font-medium text-foreground">
                          {activePhaseSubmissionMeta.submittedAt
                            ? new Date(
                                activePhaseSubmissionMeta.submittedAt,
                              ).toLocaleString("en-IN")
                            : "—"}
                        </span>
                      </p>
                      <p>
                        Submitted by:{" "}
                        <span className="font-medium text-foreground">
                          {activePhaseSubmissionMeta.submitterLabel}
                        </span>
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerDetail;
